/**
 * COL-246 — "Confirm your email" gate shown right after a room application
 * creates a CoLiviq account.
 *
 * At this point the account exists but is unconfirmed, so there is no session
 * yet — sending the applicant to the protected /dashboard route just bounces
 * them back to the home page. Instead we land them here and prompt them to
 * confirm their email; the confirmation link then opens the dashboard directly.
 */
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { MailCheck } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

const COPY: Record<"en" | "de", Record<string, string>> = {
  en: {
    title: "Confirm your email",
    description: "Your coliviq account is almost ready.",
    sentTo: "We've sent a confirmation link to",
    body: "Confirm your email to unlock your dashboard, where you'll see your application and live updates.",
    spam: "Can't find it? Check your spam folder.",
    backToHome: "Back to home",
  },
  de: {
    title: "Bestätigen Sie Ihre E-Mail",
    description: "Ihr coliviq-Konto ist fast fertig.",
    sentTo: "Wir haben einen Bestätigungslink gesendet an",
    body: "Bestätigen Sie Ihre E-Mail, um Ihr Dashboard freizuschalten, wo Sie Ihre Bewerbung und aktuelle Updates sehen.",
    spam: "Nicht gefunden? Prüfen Sie Ihren Spam-Ordner.",
    backToHome: "Zurück zur Startseite",
  },
};

const CheckEmail = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const location = useLocation();
  const email = (location.state as { email?: string } | null)?.email ?? "";
  const c = COPY[language === "de" ? "de" : "en"];

  return (
    <div className="min-h-screen pt-24 pb-20 flex items-center justify-center px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1 text-center">
          <div className="flex items-center justify-center mb-4">
            <div className="flex items-center justify-center h-16 w-16 rounded-full bg-primary/10">
              <MailCheck className="h-8 w-8 text-primary" />
            </div>
          </div>
          <CardTitle className="text-2xl font-bold">{c.title}</CardTitle>
          <CardDescription>{c.description}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="text-center space-y-2">
            <p className="text-sm text-muted-foreground">
              {c.sentTo}
              {email ? (
                <>
                  {" "}
                  <strong className="text-foreground">{email}</strong>
                </>
              ) : (
                "."
              )}
            </p>
            <p className="text-sm text-muted-foreground">{c.body}</p>
            <p className="text-xs text-muted-foreground">{c.spam}</p>
          </div>
          <Button onClick={() => navigate("/")} className="w-full">
            {c.backToHome}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default CheckEmail;
